import {Observable, shareReplay, throwError} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {environment} from '@env/environment';
import {EntitiesMetadata} from '@app/core/model/entities-metadata';
import {Page} from './model/page';
import {ChangeEntityEvent, PagedData, PagedDataItem} from './model/paged-data';

import {TokenService} from '@app/core/security/token.service';
import {AttributeMetadataModel, copyData} from '@app/core/model/integration-models';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';
import {DataAttribute} from '@app/views/admin/application-settings/models/data-attribute.model';
import {OrgsService} from '@app/core/service/orgs.service';
import {DistrictProfileModel, SchoolProfileModel} from '@app/core/model/entity-profile';
import {select, Store} from '@ngrx/store';
import {getDefaultSchema, State} from '@app/app.reducers';
import {GetDefaultSchemaAction} from '@app/core/reducers/integration/integration.actions';
import {Person, PIIData, SchoolModel} from '@app/views/databrowsing/model/rostering.models';
import {DistrictEntity, Guid, IEntity} from '@app/views/databrowsing/model/common.data.models';
import {LinkedEntities, LinkedEntityItem} from '@app/views/databrowsing/model/linked-entities.models';
import {ATTRIBUTE_COUNTER_SUFFIX, DownloadMediaTypes} from '@app/views/databrowsing/model/databrowsing-options.model';
import {EntityTotals, SchoolsEntityTotals} from '@app/views/databrowsing/model/entity-totals';
import {LineItemModel, ResultModel} from '@app/views/databrowsing/model/gradebook.models';
import {ETXUserInfo, OneTimeLoginHistoryItem} from '@app/views/databrowsing/model/one-time-user-login.model';
import {Application} from '@app/views/admin/application-settings/models/application.model';

@Injectable()
export class DataBrowsingService {

    static getPersonFriendlyName(entity: Person, includeGuid: boolean = false, addTitle: boolean = false): string {
        let givenName = entity.name ? entity.name.givenName || '' : '';
        let familyName = entity.name ? entity.name.familyName || '' : '';
        let localId = entity.localId || '';
        let emailAddress = entity.email ? entity.email.emailAddress || '' : '';
        let guid = includeGuid && entity.guid ? entity.guid.guidValue || '' : '';

        givenName = (givenName ? (givenName + (!familyName && (localId || emailAddress || guid) ? ',' : '') + ' ') : '');
        familyName = (familyName ? (familyName + ((localId || emailAddress || guid) ? ',' : '') + ' ') : '');
        localId = (localId ? (localId + ((emailAddress || guid) ? ',' : '') + ' ') : '');
        localId = localId && addTitle ? `SIS ID: ${localId}` : localId;
        emailAddress = (emailAddress ? (emailAddress + (guid ? ',' : '') + ' ') : '');
        guid = guid && addTitle ? `${environment.customer.guidTitle}: ${guid}` : guid;

        return givenName + familyName + localId + emailAddress + guid;
    }

    private readonly DEFAULT_ATTRIBUTES = new Map<string, DataAttribute[]>([
        ['School', [
            new DataAttribute('students' + ATTRIBUTE_COUNTER_SUFFIX, 'Students', DataSourceType.EduTone, 'School', 'Count', 0, -1),
            new DataAttribute('contacts' + ATTRIBUTE_COUNTER_SUFFIX, 'Contacts', DataSourceType.EduTone, 'School', 'Count', 0, -1),
            new DataAttribute('staff' + ATTRIBUTE_COUNTER_SUFFIX, 'Teachers', DataSourceType.EduTone, 'School', 'Count', 0, -1),
            new DataAttribute('rosters' + ATTRIBUTE_COUNTER_SUFFIX, 'Classes', DataSourceType.EduTone, 'School', 'Count', 0, -1),
            new DataAttribute('courses' + ATTRIBUTE_COUNTER_SUFFIX, 'Courses', DataSourceType.EduTone, 'School', 'Count', 0, -1),
            new DataAttribute('calendars' + ATTRIBUTE_COUNTER_SUFFIX, 'Calendars', DataSourceType.EduTone, 'School', 'Count', 0, -1),
            new DataAttribute('groups' + ATTRIBUTE_COUNTER_SUFFIX, 'Groups', DataSourceType.EduTone, 'School', 'Count', 0, -1),
            new DataAttribute('schoolAdmins' + ATTRIBUTE_COUNTER_SUFFIX, 'Admins', DataSourceType.EduTone, 'School', 'Count', 0, -1)
        ]],
        ['Student', [
            new DataAttribute('contacts' + ATTRIBUTE_COUNTER_SUFFIX, 'Contacts', DataSourceType.EduTone, 'Student', 'Count', 0, -1),
            // new DataAttribute('studentContacts' + ATTRIBUTE_COUNTER_SUFFIX, 'Contacts', DataSourceType.EduTone, 'Student', 'Count', 0, -1),
            new DataAttribute('rosters' + ATTRIBUTE_COUNTER_SUFFIX, 'Classes', DataSourceType.EduTone, 'Student', 'Count', 0, -1),
        ]],
        ['Contact', [new DataAttribute('students' + ATTRIBUTE_COUNTER_SUFFIX, 'Students', DataSourceType.EduTone, 'Contact', 'Count', 0, -1)]],
        ['Staff', [new DataAttribute('rosters' + ATTRIBUTE_COUNTER_SUFFIX, 'Classes', DataSourceType.EduTone, 'Staff', 'Count', 0, -1)]],
        ['SchoolAdmin', [new DataAttribute('rosters' + ATTRIBUTE_COUNTER_SUFFIX, 'Classes', DataSourceType.EduTone, 'SchoolAdmin', 'Count', 0, -1)]],
        ['Course', [new DataAttribute('rosters' + ATTRIBUTE_COUNTER_SUFFIX, 'Classes', DataSourceType.EduTone, 'Course', 'Count', 0, -1)]],
        ['Roster', [
            new DataAttribute('students' + ATTRIBUTE_COUNTER_SUFFIX, 'Students', DataSourceType.EduTone, 'Roster', 'Count', 0, -1),
            new DataAttribute('staff' + ATTRIBUTE_COUNTER_SUFFIX, 'Teachers', DataSourceType.EduTone, 'Roster', 'Count', 0, -1),
            new DataAttribute('calendars' + ATTRIBUTE_COUNTER_SUFFIX, 'Calendars', DataSourceType.EduTone, 'Roster', 'Count', 0, -1)
        ]],
        // ['Calendar', []],
        ['Group', [
            new DataAttribute('members' + ATTRIBUTE_COUNTER_SUFFIX, 'Members', DataSourceType.EduTone, 'Group', 'Count', 0, -1)
        ]],
        // ['SchoolAdmin', []]
        ['ScoreScale', [
            new DataAttribute('scoreScaleValues', 'Values', DataSourceType.EduTone, 'ScoreScale', 'String', 0, -1)
        ]]
    ]);

    // public hasStudentsScoresLoadingError$ = new BehaviorSubject<boolean>(false);

    constructor(
        private store: Store<State>,
        private tokenService: TokenService,
        private orgsService: OrgsService,
        private http: HttpClient) {
    }

    getMetadata(urlTabs: string = '/preferences/tables'): Observable<EntitiesMetadata> {
        const url = environment.datahubBaseURI + urlTabs;
        return this.http
            .get<EntitiesMetadata>(url)
            .pipe(catchError(this.handleError));
    }

    updateMetadata(metadata, urlTabs: string = '/preferences/tables') {
        const url = environment.datahubBaseURI + urlTabs;
        return this.http
            .post<EntitiesMetadata>(url, metadata)
            .pipe(catchError(this.handleError));
    }

    getDefaultSchemaAttributes(): Observable<AttributeMetadataModel[]> {
        return this.store.pipe(select(getDefaultSchema)).pipe(
            distinctUntilChanged((a: AttributeMetadataModel[], b: AttributeMetadataModel[]) =>
                JSON.stringify(a) === JSON.stringify(b)
            ),
            filter((defaultSchema: AttributeMetadataModel[]) => {
                if (!defaultSchema) {
                    this.store.dispatch(new GetDefaultSchemaAction());
                }
                return !!defaultSchema;
            }),
            map((defaultSchema: AttributeMetadataModel[]) =>
                this.addDefaultAttributes(copyData(defaultSchema))
            )
        );
    }

    getAttributesMetadata(dataSourceType: DataSourceType): Observable<AttributeMetadataModel[]> {
        return this.http
            .get<AttributeMetadataModel[]>(`${environment.datahubBaseURI}/api/v2/applications/schema/${dataSourceType}`)
            .pipe(catchError(this.handleError));
    }

    addDefaultAttributes(attrMetadata: AttributeMetadataModel[]): AttributeMetadataModel[] {
        return attrMetadata.map((attributeMetadataModel: AttributeMetadataModel) => {
            if (this.DEFAULT_ATTRIBUTES.has(attributeMetadataModel.typeModel.denom)) {
                attributeMetadataModel.propertyModel = [
                    ...attributeMetadataModel.propertyModel,
                    ...this.DEFAULT_ATTRIBUTES.get(attributeMetadataModel.typeModel.denom)
                ];
            }
            return attributeMetadataModel;
        });
    }

    getProviderDataTotals(orgGuid: string, showStage = false, applicationId?: string): Observable<Map<string, number>> {
        const queryParams = (showStage ? `?showStage=true&applicationId=${applicationId}` : '');
        const url = `${environment.datahubBaseURI}/api/v2/institutions/districts/${orgGuid}/totals${queryParams}`;
        return this.http
            .get<EntityTotals>(url)
            .pipe(
                map((totals: EntityTotals) =>
                    new Map<string, number>([
                        ['School', totals.SCHOOL],
                        ['Student', totals.STUDENT],
                        ['Contact', totals.CONTACT],
                        ['Staff', totals.STAFF],
                        ['Course', totals.COURSE],
                        ['Roster', totals.ROSTER],
                        ['Calendar', totals.CALENDAR],
                        ['Group', totals.GROUP],
                        ['SchoolAdmin', totals.SCHOOL_ADMIN],
                        ['LineItem', totals.LINE_ITEM],
                        ['Result', totals.RESULT],
                        ['AssessmentLineItem', totals.ASSESSMENT_LINE_ITEM],
                        ['AssessmentResult', totals.ASSESSMENT_RESULT],
                        ['ScoreScale', totals.SCORE_SCALE],
                        ['Category', totals.CATEGORY],
                        ['Resource', totals.RESOURCE]
                    ])
                ),
                catchError(this.handleError)
            );
        // return of<Map<string, number>>(new Map<string, number>());
    }

    getVendorDataTotals(orgGuid: string, applicationGuid?: string): Observable<Map<string, number>> {
        return this.orgsService.getDistrictProfile(orgGuid, applicationGuid)
            .pipe(map((districtProfile: DistrictProfileModel) => {
                if (DistrictProfileModel.isExpired(districtProfile)) {
                    districtProfile = new DistrictProfileModel();
                }
                return new Map<string, number>([
                    ['School', districtProfile.schoolsShared - districtProfile.schoolsSkipped],
                    ['Student', districtProfile.studentsShared - districtProfile.studentsSkipped],
                    ['Contact', districtProfile.contactsShared - districtProfile.contactsSkipped],
                    ['Staff', districtProfile.staffShared - districtProfile.staffSkipped],
                    ['Course', districtProfile.coursesShared - districtProfile.coursesSkipped],
                    ['Roster', districtProfile.sectionsShared - districtProfile.sectionsSkipped],
                    ['Calendar', districtProfile.calendarsShared - districtProfile.calendarsSkipped],
                    ['Group', districtProfile.groupsShared - districtProfile.groupsSkipped],
                    ['SchoolAdmin', districtProfile.schoolAdminsShared - districtProfile.schoolAdminsSkipped],
                    ['LineItem', districtProfile.lineItemsShared - districtProfile.lineItemsSkipped],
                    ['Result', districtProfile.resultsShared - districtProfile.resultsSkipped],
                    ['AssessmentLineItem', districtProfile.assessmentLineItemsShared - districtProfile.assessmentLineItemsSkipped],
                    ['AssessmentResult', districtProfile.assessmentResultsShared - districtProfile.assessmentResultsSkipped],
                    ['ScoreScale', districtProfile.scoreScalesShared - districtProfile.scoreScalesSkipped],
                    ['Category', districtProfile.categoriesShared - districtProfile.categoriesSkipped],
                    ['Resource', districtProfile.resourcesShared - districtProfile.resourcesSkipped]
                ]);
            }));
    }

    getProviderData<EntityType>(page: Page): Observable<PagedData<EntityType>> {
        return this.http.get<PagedData<EntityType>>(`${environment.dataApiBaseURI}/datahub/services/provider/data_browsing/v1/${page.entity}`, {
            params: page.getParams()
        })
            .pipe(
                map((pagedData: PagedData<EntityType>) =>
                    // pagedData.total = (page.offset === 0) ? pagedData.total : page.totalElements;
                    pagedData
                ), // maybe no map
                catchError(this.handleError)
            );
    }

    getVendorData<EntityType>(page: Page, accessToken: string): Observable<PagedData<EntityType>> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`
        });

        return this.http.get<PagedData<EntityType>>(`${environment.dataApiBaseURI}/datahub/services/vendor/data_browsing/v1/${page.entity}`, {
            headers: headers,
            params: page.getParams()
        })
            .pipe(
                map((pagedData: PagedData<EntityType>) =>
                    // pagedData.total = (page.offset === 0) ? pagedData.total : page.totalElements;
                    pagedData
                ), // maybe no map
                catchError(this.handleError)
            );
    }

    getProviderSchoolsData(orgGuid: string): Observable<SchoolsEntityTotals> {
        const url = `${environment.datahubBaseURI}/api/v2/institutions/districts/${orgGuid}/schools/totals`;
        return this.http
            .get<SchoolsEntityTotals>(url)
            .pipe(catchError(this.handleError));
        // return of<Map<string, number>>(new Map<string, number>());
    }


    getProviderSchoolsList(orgGuid: string, limit: number = 500): Observable<SchoolProfileModel[]> {
        return this.getProviderEntitiesList('schools', orgGuid, limit).pipe(
            map((value: PagedData<IEntity>) => value.data),
            map((schools: PagedDataItem<SchoolModel>[]) => schools.map(school => {
                const schoolProfile = new SchoolProfileModel();
                schoolProfile.schoolName = school.data.schoolName;
                schoolProfile.localId = school.data.localId;
                schoolProfile.schoolGuid = school.data.guid.guidValue;
                return schoolProfile;
            }))
        );
    }

    getProviderEntitiesList(entity: string, orgGuid: string, limit: number = 500): Observable<PagedData<IEntity>> {
        const page = new Page();
        page.entity = entity;
        page.limit = limit;
        page.orgGuid = orgGuid;

        return this.getProviderData(page);
    }

    getDownloadDataUrl(page: Page, providerGuid?: string, dataUrl: DownloadMediaTypes = DownloadMediaTypes.DATA): string {
        let url = `${environment.dataApiBaseURI}/${dataUrl.toLocaleString()}/${page.entity}/download/csv`;
        const params = [];
        if (providerGuid) {
            // params.push(`institutionId=${providerGuid}`);
        }
        // params.push(`applicationId=${page.applicationId}`);
        params.push(`access_token=${this.tokenService.getToken()}`);
        url += '?' + page.getParams() + '&' + params.join('&');

        return url;
    }

    getProviderDetails(entity: string, guid: string, showStage = false): Observable<PagedDataItem<DistrictEntity>> {
        const queryParams = (showStage ? '?showStage=true' : '');
        const url = `${environment.dataApiBaseURI}/datahub/services/provider/data_browsing/v1/${entity}/${guid}${queryParams}`;
        return this.http
            .get<PagedDataItem<DistrictEntity>>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if ((error.status === 404) && (entity === 'staffs')) {
                        return this.getProviderDetails('school_admins', guid, showStage);
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    getVendorDetails(entity: string, guid: string, accessToken: string): Observable<PagedDataItem<DistrictEntity>> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`
        });
        const url = `${environment.dataApiBaseURI}/datahub/services/vendor/data_browsing/v1/${entity}/${guid}`;
        return this.http
            .get<PagedDataItem<DistrictEntity>>(url, {headers: headers})
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if ((error.status === 404) && (entity === 'staffs')) {
                        return this.getVendorDetails('school_admins', guid, accessToken);
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    getVendorHistoryOfChanges(guid: string, accessToken: string): Observable<ChangeEntityEvent<DistrictEntity>[]> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`
        });
        const url = `${environment.dataApiBaseURI}/datahub/services/vendor/data_browsing/v1/${guid}/events?access_events=true`;
        return this.http
            .get<ChangeEntityEvent<DistrictEntity>[]>(url, {headers: headers})
            .pipe(catchError(this.handleError));
    }

    getProviderHistoryOfChanges(guid: string, showStage = false): Observable<ChangeEntityEvent<DistrictEntity>[]> {
        const queryParams = (showStage ? '?showStage=true' : '');
        const url = `${environment.dataApiBaseURI}/datahub/services/provider/data_browsing/v1/${guid}/events${queryParams}`;
        return this.http
            .get<ChangeEntityEvent<DistrictEntity>[]>(url)
            .pipe(
                map((events: ChangeEntityEvent<DistrictEntity>[]) => {
                    if (events && events.length > 0) {
                        events = events.map((event: ChangeEntityEvent<DistrictEntity>) => {
                            event.isStageData = showStage;
                            return event;
                        });
                    }

                    return events;
                }),
                catchError(this.handleError)
            );
    }

    getOneTimeLoginLinkHistory(): Observable<OneTimeLoginHistoryItem[]> {
        const url = `${environment.etxBaseURI}/account/onetimelogin/statistic`;
        return this.http.get<OneTimeLoginHistoryItem[]>(url)
            .pipe(
                map((response: OneTimeLoginHistoryItem[]) => response),
                catchError(this.handleError)
            );
    }

    getETXUserInfo(guid: string): Observable<ETXUserInfo> {
        const url = `${environment.etxBaseURI}/account/onetimelogin/userinfo/${guid}`;
        return this.http.get<ETXUserInfo>(url)
            .pipe(
                map((response: ETXUserInfo) => response),
                catchError(this.handleError)
            );
    }

    // getProviderStudentsGrades(districtGuid, studentGuid, showStage = false): Observable<{[key: string]: Metadata[]}> {
    //     const queryParams = (showStage ? '?showStage=true' : '');
    //     const url = `${environment.dataApiBaseURI}/datahub/services/provider/data_browsing/v1/students/${districtGuid}/${studentGuid}/sectionenrollmentmetadata${queryParams}`;
    //
    //     return this.http
    //         .get<{[key: string]: Metadata[]}>(url)
    //         .pipe(catchError(this.handleError));
    // }

    // getVendorStudentGrades(districtGuid, studentGuid): Observable<{[key: string]: Metadata[]}> {
    //     const url = `${environment.dataApiBaseURI}/datahub/services/vendor/data_browsing/v1/students/${districtGuid}/${studentGuid}/sectionenrollmentmetadata`;
    //
    //     return this.http
    //         .get<{[key: string]: Metadata[]}>(url)
    //         .pipe(catchError(this.handleError));
    // }

    // getProviderSectionsEnrollmentByStudent(districtGuid, studentGuid, showStage = false): Observable<ChangeEntityEvent<RosterStudentEnrollmentModel>[]> {
    //     const queryParams = (showStage ? '?showStage=true' : '');
    //     const url = `${environment.dataApiBaseURI}/datahub/services/provider/data_browsing/v1/${districtGuid}/${studentGuid}/sectionenrollmentevents${queryParams}`;
    //
    //     return this.http
    //         .get<ChangeEntityEvent<RosterStudentEnrollmentModel>[]>(url)
    //         .pipe(catchError(this.handleError));
    // }

    // getVendorSectionsEnrollmentByStudent(districtGuid, studentGuid): Observable<ChangeEntityEvent<RosterStudentEnrollmentModel>[]> {
    //     const url = `${environment.dataApiBaseURI}/datahub/services/vendor/data_browsing/v1/${districtGuid}/${studentGuid}/sectionenrollmentevents`;
    //
    //     return this.http
    //         .get<ChangeEntityEvent<RosterStudentEnrollmentModel>[]>(url)
    //         .pipe(catchError(this.handleError));
    // }

    getProviderResultsByStudent(studentGuid, showStage = false): Observable<PagedData<ResultModel>> {
        const queryParams = (showStage ? '?showStage=true' : '');
        const url = `${environment.dataApiBaseURI}/datahub/services/provider/data_browsing/v1/results/byStudent/${studentGuid}${queryParams}`;

        return this.http
            .get<PagedData<ResultModel>>(url)
            .pipe(catchError(this.handleError));
    }

    getVendorResultsByStudent(studentGuid, accessToken: string): Observable<PagedData<ResultModel>> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`
        });
        const url = `${environment.dataApiBaseURI}/datahub/services/vendor/data_browsing/v1/results/byStudent/${studentGuid}`;

        return this.http
            .get<PagedData<ResultModel>>(url, {headers: headers})
            .pipe(catchError(this.handleError));
    }

    getProviderResultsByLineItem(lineItemGuid, showStage = false): Observable<PagedData<ResultModel>> {
        const queryParams = (showStage ? '?showStage=true' : '');
        const url = `${environment.dataApiBaseURI}/datahub/services/provider/data_browsing/v1/results/byLineItem/${lineItemGuid}${queryParams}`;

        return this.http
            .get<PagedData<ResultModel>>(url)
            .pipe(catchError(this.handleError));
    }

    getVendorResultsByLineItem(lineItemGuid, accessToken: string): Observable<PagedData<ResultModel>> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`
        });
        const url = `${environment.dataApiBaseURI}/datahub/services/vendor/data_browsing/v1/results/byLineItem/${lineItemGuid}`;

        return this.http
            .get<PagedData<ResultModel>>(url, {headers: headers})
            .pipe(catchError(this.handleError));
    }

    getProviderLineItemsByRoster(rosterGuid, showStage = false): Observable<PagedData<LineItemModel>> {
        const queryParams = (showStage ? '?showStage=true' : '');
        const url = `${environment.dataApiBaseURI}/datahub/services/provider/data_browsing/v1/line_items/byRoster/${rosterGuid}${queryParams}`;

        return this.http
            .get<PagedData<LineItemModel>>(url)
            .pipe(catchError(this.handleError));
    }

    getVendorLineItemsByRoster(rosterGuid, accessToken: string): Observable<PagedData<LineItemModel>> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`
        });
        const url = `${environment.dataApiBaseURI}/datahub/services/vendor/data_browsing/v1/line_items/byRoster/${rosterGuid}`;

        return this.http
            .get<PagedData<LineItemModel>>(url, {headers: headers})
            .pipe(catchError(this.handleError));
    }

    getApplicationByClientGid(clientGid: string, districtGuid: string): Observable<Application> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/${districtGuid}/${clientGid}`;
        return this.http
            .get<Application>(url)
            .pipe(catchError(this.handleError));
    }

    processEntityDetailsData(
        data: PagedDataItem<DistrictEntity>,
        entityType: string
    ): PagedDataItem<DistrictEntity> {
        if (data && data.linkedEntities) {
            data.linkedEntities = this.filterNullLinks(data.linkedEntities);

            if (entityType === 'Student') {
                if (data.linkedEntities.enrollment) {
                    data.linkedEntities.school = data.linkedEntities.enrollment;
                }

                // this.sortDataObjLinks(data, 'otherEnrollments', 'schoolId');
                this.sortDataLinks(data, 'otherEnrollments');
                this.sortDataLinks(data, 'studentContacts');
            }

            if (entityType === 'Contact') {
                // this.sortDataObjLinks(data, 'relationships', 'studentId');
                this.sortDataLinks(data, 'relationships');
            }

            if ((entityType === 'Staff') || (entityType === 'SchoolAdmin')) {
                if (data.linkedEntities.primaryAssignment) {
                    data.linkedEntities.school = data.linkedEntities.primaryAssignment;
                }

                this.sortDataLinks(data, 'otherAssignments');
            }

            if (entityType === 'Roster') {
                this.sortDataLinks(data, 'otherStaffs');
                this.sortDataLinks(data, 'students');
                this.sortDataLinks(data, 'calendars');
            }

            if (entityType === 'Calendar') {
                this.sortDataLinks(data, 'children');
            }

            if (entityType === 'Group') {
                this.sortDataLinks(data, 'members');
            }

            if (['Student', 'Staff', 'Course', 'Calendar', 'SchoolAdmin'].indexOf(entityType) > -1) {
                this.sortDataLinks(data, 'rosters');
            }

            if (['Student', 'Contact', 'Staff', 'SchoolAdmin'].indexOf(entityType) > -1) {
                this.sortDataLinks(data, 'groupMemberships');

                (<Person>data.data).piiData = PIIData.getPIIDataList(data);
                if ((<Person>data.data).piiData) {
                    (<Person>data.data).piiData.forEach((piiDataItem: PIIData) =>
                        piiDataItem.application$ =
                            this.getApplicationByClientGid(piiDataItem.clientGid, Guid.getValue(data.data.districtId))
                                .pipe(shareReplay())
                    );
                }
            }

            if (['Contact', 'Course', 'Calendar'].indexOf(entityType) > -1) {
                this.sortDataLinks(data, 'otherSchoolIds');
            }
        }
        return data;
    }

    private sortLinks(data: PagedDataItem<DistrictEntity>, attribute: string) {
        if ((data.linkedEntities[attribute].length === 1) && !data.linkedEntities[attribute][0]) {
            data.linkedEntities[attribute] = [];
        }
        data.linkedEntities[attribute].forEach((link: LinkedEntityItem, index: number) => link._originalIndex = index);
        data.linkedEntities[attribute].sort((link1: LinkedEntityItem, link2: LinkedEntityItem) =>
            (link1.name || '').toLowerCase().localeCompare((link2.name || '').toLowerCase())
        );

        const order = new Array(data.linkedEntities[attribute].length);
        data.linkedEntities[attribute].forEach((link: LinkedEntityItem, index: number) => order[link._originalIndex] = index);

        const dataList = [];
        (data.data[attribute] || []).forEach((item: any, index: number) => dataList[order[index]] = item);
        data.data[attribute] = dataList;
    }

    private sortDataLinks(data: PagedDataItem<DistrictEntity>, attribute: string) {
        if (data.linkedEntities[attribute] && (data.linkedEntities[attribute].length > 0)) {
            this.sortLinks(data, attribute);

            // const list: string[] = data.linkedEntities[attribute].map((link: LinkedEntityItem) => link.guid);

            // data.data[attribute].forEach((guidObj: any) => {
            //     const guid: string = (guidObj && typeof guidObj === 'object') ? guidObj['guidValue'] : guidObj;
            //     const existing = list.find((listGuid: string) => listGuid === guid);
            //     if (!existing) {
            //         list.push(guid);
            //     }
            // });

            // data.data[attribute] = list;
        }
    }

    private sortDataObjLinks(data: PagedDataItem<DistrictEntity>, attribute: string, objGuidAttribute: string) {
        if (data.linkedEntities[attribute] && (data.linkedEntities[attribute].length > 0)) {
            this.sortLinks(data, attribute);

            // const list = data.linkedEntities[attribute]
            //     .map((link: LinkedEntityItem) => {
            //         let objData = data.data[attribute].find((obj: any) => obj[objGuidAttribute] && (obj[objGuidAttribute] === link.guid));
            //         if (objData === undefined) {
            //             objData = {};
            //             objData[objGuidAttribute] = link.guid;
            //         }
            //         return objData;
            //     })
            //     .filter((obj: any) => !!obj);
            //
            // data.data[attribute].forEach((obj: any) => {
            //     if (obj && obj[objGuidAttribute]) {
            //         const guid: string = (obj[objGuidAttribute] && typeof obj[objGuidAttribute] === 'object') ? obj[objGuidAttribute]['guidValue'] : obj[objGuidAttribute];
            //         const existing = list.find((listObj: any) => listObj[objGuidAttribute] === guid);
            //         if (!existing) {
            //             list.push(obj);
            //         }
            //     }
            // });
            //
            // data.data[attribute] = list;
        }
    }

    filterNullLinks(linksData: LinkedEntities): LinkedEntities {
        if (linksData && typeof linksData === 'object') {
            Object.keys(linksData).forEach(linkName => {
                if (linksData[linkName] && typeof linksData[linkName] === 'object' && linksData[linkName] instanceof Array) {
                    linksData[linkName] = linksData[linkName].filter((linkData: any) => linkData !== null);
                }
            });
        }
        return linksData;
    }

    private handleError(error: HttpErrorResponse) {
        let errorMessage = 'Something bad happened, please try again later.';
        if (error.error && error.error.message) {
            errorMessage = error.error.message;
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(`Backend returned ${error.status}, ${error.error}`);
        }
        return throwError(errorMessage);
    }
}
